import React from 'react';
import { Box, Button, Stack, Typography, useTheme } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import Logo from './Logo';

/** Nautilus Console tokens — aligned with DESIGN.md */
const N = {
  deepOcean: '#0f2134',
  sand: '#ccba9b',
  abyssal: '#0a1622',
  surfaceLight: '#ffffff',
  surfaceDark: '#0f2134',
  borderLight: '#e0e0e0',
  borderDark: '#2c3e50',
  mutedLight: '#546e7a',
  mutedDark: '#b0bec5',
};

export interface AccessDeniedSecondaryAction {
  label: string;
  to: string;
}

export interface AccessDeniedProps {
  title?: string;
  message?: string;
  /** Optional secondary action link */
  secondaryAction?: AccessDeniedSecondaryAction;
  /** @deprecated Use secondaryAction instead */
  showProjectsLink?: boolean;
}

const AccessDenied: React.FC<AccessDeniedProps> = ({
  title = 'Access denied',
  message = 'You do not have permission to access this page.',
  secondaryAction,
  showProjectsLink = false,
}) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const navigate = useNavigate();

  const effectiveSecondaryAction =
    secondaryAction ?? (showProjectsLink ? { label: 'My projects', to: '/projects' } : undefined);

  const panelBorder = isDark ? N.borderDark : N.borderLight;
  const surfaceBg = isDark ? N.surfaceDark : N.surfaceLight;
  const pageBg = isDark ? N.abyssal : '#f5f5f5';
  const ink = isDark ? '#ffffff' : N.deepOcean;
  const muted = isDark ? N.mutedDark : N.mutedLight;

  const buttonSx = {
    textTransform: 'uppercase' as const,
    letterSpacing: '0.05em',
    fontWeight: 600,
    fontSize: '0.75rem',
    borderRadius: '8px',
    px: 3,
    py: 1.5,
    boxShadow: 'none',
    '&:hover': { boxShadow: 'none' },
    '&:focus-visible': {
      outline: `2px solid ${isDark ? N.sand : N.deepOcean}`,
      outlineOffset: 2,
    },
  };

  return (
    <Box
      role="alert"
      aria-live="polite"
      sx={{
        minHeight: 'calc(100dvh - 140px)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        px: 2,
        py: 4,
        bgcolor: pageBg,
      }}
    >
      <Box
        sx={{
          width: '100%',
          maxWidth: 440,
          textAlign: 'center',
          p: { xs: 3, sm: 4 },
          borderRadius: '8px',
          border: `1px solid ${panelBorder}`,
          bgcolor: surfaceBg,
          boxShadow: 'none',
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 4 }}>
          <Logo size="large" clickable={false} showText={false} invertColors />
        </Box>

        <Typography
          component="h1"
          sx={{
            fontSize: '1.25rem',
            fontWeight: 600,
            letterSpacing: '0.05em',
            textTransform: 'uppercase',
            color: ink,
            mb: 1,
            lineHeight: 1.3,
          }}
        >
          {title}
        </Typography>

        <Typography
          sx={{
            color: muted,
            fontSize: '0.9375rem',
            fontWeight: 400,
            lineHeight: 1.5,
            mb: 4,
            mx: 'auto',
            maxWidth: '36ch',
          }}
        >
          {message}
        </Typography>

        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={1.5}
          justifyContent="center"
          alignItems="stretch"
        >
          <Button
            variant="contained"
            onClick={() => navigate('/')}
            sx={{
              ...buttonSx,
              bgcolor: N.sand,
              color: N.deepOcean,
              '&:hover': {
                ...buttonSx['&:hover'],
                bgcolor: 'rgba(204, 186, 155, 0.85)',
              },
            }}
          >
            Back to workspace
          </Button>
          {effectiveSecondaryAction && (
            <Button
              variant="outlined"
              onClick={() => navigate(effectiveSecondaryAction.to)}
              sx={{
                ...buttonSx,
                borderColor: panelBorder,
                color: isDark ? N.sand : N.deepOcean,
                bgcolor: 'transparent',
                '&:hover': {
                  ...buttonSx['&:hover'],
                  borderColor: isDark ? N.sand : N.deepOcean,
                  bgcolor: isDark ? 'rgba(204, 186, 155, 0.08)' : 'rgba(15, 33, 52, 0.04)',
                },
              }}
            >
              {effectiveSecondaryAction.label}
            </Button>
          )}
        </Stack>
      </Box>
    </Box>
  );
};

export default AccessDenied;
